/**
 * Platform AI usage / billing analytics (B0.4 / B3.2).
 */
import { get } from '@core/api/client';
import client from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type {
  UsagePool,
  UsageSummary,
  UsageTokenTotals,
} from './billingUsage.service';

export interface PlatformBillingUsageQuery {
  tenantId?: string | null;
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
  pool?: UsagePool;
  page?: number;
  limit?: number;
}

export interface UsageByTenantRow extends UsageTokenTotals {
  tenantId: string;
}

export interface PlatformLlmCredentialStatusRow {
  tenantId: string;
  provider: string;
  hasTenantKey: boolean;
  tenantStatus: string | null;
  tenantValidationStatus: string | null;
  tenantDisplayHint: string | null;
  agentOverrideCount: number;
  updatedAt: string | null;
}

function toQuery(params: PlatformBillingUsageQuery): string {
  const q = new URLSearchParams();
  if (params.tenantId) q.set('tenantId', params.tenantId);
  if (params.agentId) q.set('agentId', params.agentId);
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.pool) q.set('pool', params.pool);
  if (params.page != null) q.set('page', String(params.page));
  if (params.limit != null) q.set('limit', String(params.limit));
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const platformBillingUsageService = {
  summary(params: PlatformBillingUsageQuery = {}): Promise<UsageSummary> {
    return get<UsageSummary>(
      `${endpoints.platformBillingUsage.summary}${toQuery(params)}`,
    );
  },

  byTenant(params: PlatformBillingUsageQuery = {}): Promise<UsageByTenantRow[]> {
    return get<UsageByTenantRow[]>(
      `${endpoints.platformBillingUsage.byTenant}${toQuery(params)}`,
    );
  },

  credentialsStatus(): Promise<PlatformLlmCredentialStatusRow[]> {
    return get<PlatformLlmCredentialStatusRow[]>(
      endpoints.platformBillingUsage.credentialsStatus,
    );
  },

  async exportCsv(params: PlatformBillingUsageQuery = {}): Promise<void> {
    const res = await client.get<Blob>(
      `${endpoints.platformBillingUsage.exportCsv}${toQuery(params)}`,
      { responseType: 'blob' },
    );
    downloadBlob(res.data, 'ai-usage-platform.csv');
  },
} as const;
